import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { BRAND } from '../../config/brand';

export interface MosaicItem {
  src: string;
  alt: string;
  span?: 'small' | 'medium' | 'large';
  caption?: string;
}

interface ImageMosaicProps {
  title?: string;
  subtitle?: string;
  items: MosaicItem[];
}

const spanHeights = {
  small: { xs: 200, md: 220 },
  medium: { xs: 240, md: 320 },
  large: { xs: 280, md: 460 },
};

export const ImageMosaic: React.FC<ImageMosaicProps> = ({
  title = 'Our World',
  subtitle,
  items,
}) => (
  <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: BRAND.colors.creamLight }}>
    <Container maxWidth="lg">
      <Typography
        variant="h3"
        align="center"
        sx={{ fontWeight: 800, color: BRAND.colors.navy, mb: 1 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          align="center"
          color="text.secondary"
          sx={{ mb: 5, maxWidth: 640, mx: 'auto' }}
        >
          {subtitle}
        </Typography>
      )}
      <Grid container spacing={2}>
        {items.map((item, index) => {
          const span = item.span || 'medium';
          const gridSize =
            span === 'large' ? { xs: 12, md: 8 } : span === 'small' ? { xs: 6, md: 4 } : { xs: 12, sm: 6, md: 4 };

          return (
            <Grid item {...gridSize} key={`${item.src}-${index}`}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: spanHeights[span],
                  boxShadow: '0 12px 32px rgba(27,77,137,0.15)',
                  border: `3px solid ${BRAND.colors.white}`,
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
              >
                <Box
                  component="img"
                  src={item.src}
                  alt={item.alt}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {item.caption && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 2,
                      background: 'linear-gradient(transparent, rgba(15,52,96,0.85))',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: BRAND.colors.cream, fontWeight: 600 }}>
                      {item.caption}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  </Box>
);

export default ImageMosaic;
